import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HeartIcon, ShieldCheckIcon, StarIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';

const FEATURES = [
  { icon: HeartIcon,               title: 'Verified Profiles', desc: 'Every profile verified for authenticity. Your safety is our priority.' },
  { icon: ShieldCheckIcon,         title: 'Privacy Protected', desc: 'Your personal details shown only to matched members.' },
  { icon: StarIcon,                title: 'Smart Matching',    desc: 'AI-powered compatibility matching based on denomination, education and values.' },
  { icon: ChatBubbleLeftRightIcon, title: 'Instant Chat',      desc: 'Chat securely once both profiles accept interest.' },
];
const TESTIMONIALS = [
  { name: 'Priya & David',  location: 'Vijayawada', text: 'Found my life partner in just 3 months. Praise God!' },
  { name: 'Sneha & Samuel', location: 'Hyderabad',  text: 'This platform truly understands Christian values. Highly recommended!' },
  { name: 'Ananya & John',  location: 'Guntur',     text: 'Beautiful experience. Met my soulmate through this wonderful app.' },
];
const STATS = [['50,000+', 'Registered Profiles'], ['5,000+', 'Happy Couples'], ['100%', 'Verified Profiles'], ['24/7', 'Support']];
const PLANS = [
  { name: 'Silver',   price: '₹299',  period: '/month', features: ['10 contacts/month', 'Chat access', 'View profiles'] },
  { name: 'Gold',     price: '₹599',  period: '/month', features: ['30 contacts/month', 'Chat access', 'Horoscope match', 'AI compatibility'], popular: true },
  { name: 'Platinum', price: '₹999',  period: '/month', features: ['Unlimited contacts', 'Priority listing', 'All premium features', 'Dedicated support'] },
];

const reveal = { hidden: { opacity: 0, y: 28 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.2, 0.7, 0.2, 1] } } };
const Reveal = ({ children, delay = 0, className = '' }) => (
  <motion.div className={className} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.25 }} transition={{ delay }}>{children}</motion.div>
);

const HEADLINES = [
  ['Find Your God-Given', 'Life Partner'],
  ['Two Hearts,', 'One Faith'],
  ['Blessed Unions', 'in Christ'],
  ['Begin a Journey', 'Made in Heaven'],
];

/* ── Real 3D WebGL hero: interlocking gold rings + radiant cross ── */
function Hero3D() {
  const mountRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const THREE = window.THREE;
    const mount = mountRef.current;
    if (!THREE || !mount) { setFailed(true); return; }

    let raf;
    const W = () => mount.clientWidth || 420;
    const H = () => mount.clientHeight || 320;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(42, W() / H(), 0.1, 100);
    camera.position.set(0, 0.2, 6.4);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(W(), H());
    if (THREE.sRGBEncoding) renderer.outputEncoding = THREE.sRGBEncoding;
    mount.appendChild(renderer.domElement);

    // Environment map (procedural gradient) → metallic reflections
    let envTex = null;
    try {
      const pmrem = new THREE.PMREMGenerator(renderer);
      const cv = document.createElement('canvas'); cv.width = 32; cv.height = 128;
      const ctx = cv.getContext('2d');
      const g = ctx.createLinearGradient(0, 0, 0, 128);
      g.addColorStop(0, '#fff4d6'); g.addColorStop(0.45, '#9a824f');
      g.addColorStop(0.7, '#2a3b63'); g.addColorStop(1, '#0a1428');
      ctx.fillStyle = g; ctx.fillRect(0, 0, 32, 128);
      const tex = new THREE.CanvasTexture(cv);
      tex.mapping = THREE.EquirectangularReflectionMapping;
      envTex = pmrem.fromEquirectangular(tex).texture;
      scene.environment = envTex;
      tex.dispose(); pmrem.dispose();
    } catch (e) { /* lights still provide shading */ }

    // Lights
    scene.add(new THREE.AmbientLight(0x5b6f9c, 0.55));
    const key = new THREE.DirectionalLight(0xffffff, 1.15); key.position.set(3, 5, 4); scene.add(key);
    const warm = new THREE.PointLight(0xffce7a, 1.5, 30); warm.position.set(-4, 1.5, 3); scene.add(warm);
    const cool = new THREE.PointLight(0x6fa8ff, 1.0, 30); cool.position.set(4, -2, 2.5); scene.add(cool);

    const gold = new THREE.MeshStandardMaterial({
      color: 0xd7a23c, metalness: 1.0, roughness: 0.22,
      emissive: 0x3a2606, emissiveIntensity: 0.18,
    });
    const goldBright = new THREE.MeshStandardMaterial({
      color: 0xf0c560, metalness: 1.0, roughness: 0.18,
      emissive: 0x6b4a12, emissiveIntensity: 0.5,
    });

    const root = new THREE.Group(); scene.add(root);

    // Two interlocking rings
    const ringGeo = new THREE.TorusGeometry(1.02, 0.16, 28, 110);
    const ringA = new THREE.Mesh(ringGeo, gold);
    ringA.position.set(-0.62, -0.25, 0); ringA.rotation.set(0.55, -0.35, 0.1);
    const ringB = new THREE.Mesh(ringGeo, gold);
    ringB.position.set(0.62, -0.25, 0.05); ringB.rotation.set(0.55, 0.35, -0.1);
    root.add(ringA, ringB);

    // Radiant cross above
    const cross = new THREE.Group();
    const vert = new THREE.Mesh(new THREE.BoxGeometry(0.2, 1.05, 0.2), goldBright);
    const horiz = new THREE.Mesh(new THREE.BoxGeometry(0.68, 0.2, 0.2), goldBright);
    horiz.position.y = 0.22;
    cross.add(vert, horiz);
    cross.position.set(0, 1.65, 0); cross.scale.setScalar(0.82);
    root.add(cross);
    const halo = new THREE.PointLight(0xffe6a8, 1.4, 8); halo.position.set(0, 1.7, 1.2); scene.add(halo);

    // Sparkle particles
    const N = 140; const pos = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) {
      const r = 2.4 + Math.random() * 1.9, a = Math.random() * Math.PI * 2, b = Math.acos(2 * Math.random() - 1);
      pos[i*3] = r * Math.sin(b) * Math.cos(a);
      pos[i*3+1] = r * Math.cos(b) * 0.7;
      pos[i*3+2] = r * Math.sin(b) * Math.sin(a) - 1;
    }
    const pg = new THREE.BufferGeometry();
    pg.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    const pm = new THREE.PointsMaterial({ color: 0xffe9b0, size: 0.05, transparent: true, opacity: 0.9, blending: THREE.AdditiveBlending, depthWrite: false });
    const sparks = new THREE.Points(pg, pm); scene.add(sparks);

    // Subtle pointer parallax
    let mx = 0, my = 0;
    const onMove = (e) => {
      const r = mount.getBoundingClientRect();
      mx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      my = ((e.clientY - r.top) / r.height - 0.5) * 2;
    };
    mount.addEventListener('mousemove', onMove);

    const clock = new THREE.Clock();
    const animate = () => {
      const t = clock.getElapsedTime();
      root.rotation.y = Math.sin(t * 0.35) * 0.45;
      root.position.y = Math.sin(t * 0.9) * 0.06;
      cross.rotation.y = Math.sin(t * 0.7) * 0.6;
      goldBright.emissiveIntensity = 0.4 + Math.sin(t * 2) * 0.22;
      sparks.rotation.y -= 0.0011;
      pm.opacity = 0.6 + Math.sin(t * 1.6) * 0.3;
      camera.position.x += (mx * 0.5 - camera.position.x) * 0.04;
      camera.position.y += (0.2 - my * 0.3 - camera.position.y) * 0.04;
      camera.lookAt(0, 0.25, 0);
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    const onResize = () => { camera.aspect = W() / H(); camera.updateProjectionMatrix(); renderer.setSize(W(), H()); };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      mount.removeEventListener('mousemove', onMove);
      ringGeo.dispose(); pg.dispose(); pm.dispose(); gold.dispose(); goldBright.dispose();
      if (envTex) envTex.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
    };
  }, []);

  if (failed) {
    // Graceful fallback if WebGL/Three.js unavailable
    return <div className="hero3d-fallback">💍<img src="/cross-icon.png" alt="Cross" className="cross-em w-8 h-8 rounded object-cover inline-block" /></div>;
  }
  return <div ref={mountRef} className="hero3d" aria-hidden="true" />;
}

function RotatingHeadline() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI(p => (p + 1) % HEADLINES.length), 3600);
    return () => clearInterval(t);
  }, []);
  const [top, accent] = HEADLINES[i];
  return (
    <div style={{ minHeight: 120 }}>
      <motion.h1 key={i}
        initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="font-heading text-4xl md:text-6xl font-bold leading-tight">
        {top}<br /><span className="shimmer-gold">{accent}</span>
      </motion.h1>
    </div>
  );
}

export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* ── Header ── */}
      <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? 'bg-primary-900/90 backdrop-blur-md shadow-lg py-3' : 'bg-transparent py-5'}`}>
        <div className="max-w-7xl mx-auto px-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/cross-icon.png" alt="Cross" className="w-7 h-7 md:w-8 md:h-8 rounded object-cover" />
            <span className="text-white font-heading font-bold text-xl md:text-2xl tracking-tight">Holy<span className="shimmer-gold">weds</span></span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-primary-100 hover:text-white text-sm font-medium transition-colors">Sign In</Link>
            <Link to="/register" className="btn-gold text-sm py-2 px-4">Register Free</Link>
          </div>
        </div>
      </header>

      {/* ── Hero with real 3D scene ── */}
      <section className="aurora stars relative overflow-hidden text-white pt-28 pb-24 px-5">
        <img src="/cross-icon.png" alt="Cross" className="floaty w-8 h-8 rounded object-cover" style={{ left: '8%',  top: '24%', animationDelay: '0s' }} />
        <span className="floaty text-2xl" style={{ right: '10%', top: '30%', animationDelay: '1.5s' }}>✨</span>
        <span className="floaty text-2xl" style={{ left: '14%', bottom: '20%', animationDelay: '2.4s' }}>💍</span>
        <span className="floaty text-3xl" style={{ right: '14%', bottom: '24%', animationDelay: '0.8s' }}>🕊️</span>

        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 glass px-4 py-2 rounded-full text-sm mb-2">
            <img src="/cross-icon.png" alt="Cross" className="w-4 h-4 rounded object-cover" /><span>Exclusively for the Telugu Christian Community</span><img src="/cross-icon.png" alt="Cross" className="w-4 h-4 rounded object-cover" />
          </motion.div>

          <Hero3D />
          <RotatingHeadline />

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 0.7 }}
            className="text-primary-100 text-lg md:text-xl mt-4 mb-8 max-w-2xl mx-auto leading-relaxed">
            The most trusted Christian matrimony platform for Andhra Pradesh &amp; Telangana. Join thousands of families who found their perfect match.
          </motion.p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register" className="btn-gold text-lg px-8 py-3 w-full sm:w-auto text-center">Register for Free</Link>
            <Link to="/login" className="glass hover:bg-white/20 text-white font-semibold text-lg px-8 py-3 rounded-lg transition-all w-full sm:w-auto text-center">Sign In</Link>
          </div>
          <p className="text-primary-200 text-sm mt-5">✨ First 30 days completely free. No credit card required.</p>
        </div>
        <div className="absolute -bottom-px inset-x-0 h-12 bg-white" style={{ clipPath: 'ellipse(75% 100% at 50% 100%)' }} />
      </section>

      {/* ── Stats ── */}
      <section className="bg-white">
        <div className="max-w-5xl mx-auto px-5 py-12 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {STATS.map(([num, label], i) => (
            <Reveal key={label} delay={i * 0.08}>
              <p className="font-heading font-bold text-3xl md:text-4xl text-primary">{num}</p>
              <p className="text-gray-500 text-sm mt-1">{label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── Features ── */}
      <section className="py-20 px-5 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-5xl mx-auto">
          <Reveal className="text-center mb-14">
            <p className="text-gold-600 font-semibold tracking-wide uppercase text-xs mb-2">Why Holyweds</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-3">Built on Faith &amp; Trust</h2>
            <p className="text-gray-500 max-w-xl mx-auto">Designed specifically for the Telugu Christian community, with your values at the core.</p>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map(({ icon: Icon, title, desc }, i) => (
              <Reveal key={title} delay={i * 0.08}>
                <div className="card lift p-7 text-center h-full border-t-4 border-t-gold">
                  <div className="w-14 h-14 bg-gradient-to-br from-primary-50 to-gold-100 rounded-2xl flex items-center justify-center mx-auto mb-5"><Icon className="w-7 h-7 text-primary" /></div>
                  <h3 className="font-heading font-semibold text-gray-900 mb-2 text-lg">{title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Plans ── */}
      <section id="pricing" className="py-20 px-5 bg-white">
        <div className="max-w-5xl mx-auto">
          <Reveal className="text-center mb-14">
            <p className="text-gold-600 font-semibold tracking-wide uppercase text-xs mb-2">Pricing</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-3">Simple, Honest Pricing</h2>
            <p className="text-gray-500">First month free — no commitments.</p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            {PLANS.map(({ name, price, period, features, popular }, i) => (
              <Reveal key={name} delay={i * 0.1}>
                <div className={`card lift p-7 relative h-full ${popular ? 'border-2 border-gold ring-2 ring-gold-200 md:-translate-y-3' : 'border border-gray-200'}`}>
                  {popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold-gradient text-white text-xs font-bold px-4 py-1 rounded-full">★ MOST POPULAR</span>}
                  <h3 className="font-heading font-bold text-xl text-gray-900 mb-1">{name}</h3>
                  <p className="text-4xl font-bold text-primary mb-1">{price}<span className="text-sm text-gray-400 font-normal">{period}</span></p>
                  <ul className="mt-5 space-y-3 mb-7">
                    {features.map((f) => (<li key={f} className="flex items-center gap-2 text-sm text-gray-600"><span className="text-green-500 font-bold">✓</span> {f}</li>))}
                  </ul>
                  <Link to="/register" className={`block text-center py-2.5 rounded-lg font-semibold text-sm transition-all ${popular ? 'btn-gold' : 'btn-outline'}`}>Get Started</Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="py-20 px-5 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-5xl mx-auto">
          <Reveal className="text-center mb-14">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-3 flex items-center justify-center gap-2">Blessed Unions <img src="/cross-icon.png" alt="Cross" className="w-7 h-7 rounded object-cover inline-block" /></h2>
            <p className="text-gray-500">Real stories from our happy families.</p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map(({ name, location, text }, i) => (
              <Reveal key={name} delay={i * 0.1}>
                <div className="card lift p-7 h-full">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-gold-100 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl">💒</div>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4 italic text-center">“{text}”</p>
                  <p className="font-semibold text-gray-900 text-center">{name}</p>
                  <p className="text-gold-600 text-xs text-center mt-0.5">{location}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="aurora stars relative overflow-hidden py-20 px-5 text-center">
        <Reveal className="max-w-2xl mx-auto relative z-10">
          <div className="text-4xl mb-4">💍</div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">Your Story Begins Here</h2>
          <p className="text-primary-100 mb-8">Join the largest Telugu Christian matrimony platform and find your God-chosen partner.</p>
          <Link to="/register" className="btn-gold text-lg px-10 py-3 inline-block">Start for Free Today</Link>
        </Reveal>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-primary-900 text-primary-200 py-10 px-5 text-center text-sm">
        <div className="flex items-center justify-center gap-2 mb-3">
          <img src="/cross-icon.png" alt="Cross" className="w-6 h-6 rounded object-cover" />
          <span className="text-white font-heading font-bold text-xl">Holy<span className="shimmer-gold">weds</span></span>
        </div>
        <p className="mb-2">Exclusively for the Telugu Christian Community</p>
        <p>Andhra Pradesh &amp; Telangana | <a href="mailto:contact@holyweds.com" className="hover:text-white underline-offset-2 hover:underline">contact@holyweds.com</a> | <a href="tel:9494623015" className="hover:text-white underline-offset-2 hover:underline">+91 94946 23015</a></p>
        <p className="mt-2 text-xs text-primary-300">Legal Name: Vikas Sarma Rapaka, trading as Holyweds &middot; Door No. 14-14-15, Bethany Peta, Mission Hospital Road, Bhimavaram, West Godavari, Andhra Pradesh – 534201, India</p>
        <p className="mt-3 text-xs flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
          <a href="/contact/" className="hover:text-white underline-offset-2 hover:underline">Contact Us</a>
          <span className="text-primary-400">·</span>
          <a href="/privacy-policy/" className="hover:text-white underline-offset-2 hover:underline">Privacy Policy</a>
          <span className="text-primary-400">·</span>
          <a href="/terms-and-conditions/" className="hover:text-white underline-offset-2 hover:underline">Terms &amp; Conditions</a>
          <span className="text-primary-400">·</span>
          <a href="/refund-policy/" className="hover:text-white underline-offset-2 hover:underline">Refund &amp; Cancellation Policy</a>
        </p>
        <p className="mt-4 text-xs text-primary-300">© {new Date().getFullYear()} Holyweds. All rights reserved.</p>
      </footer>
    </div>
  );
}
